/**
 * Kanban Column Component
 * 
 * Represents a single status column in the Kanban board.
 * Contains task cards and handles drag-drop interactions.
 */

import React from 'react';
import { Button } from 'react-bootstrap';
import { TaskCard, BoardColumn } from '../../types';
import TaskCardComponent from './TaskCardComponent';
import './KanbanColumn.css';

interface KanbanColumnProps {
  column: BoardColumn;
  tasks: TaskCard[];
  onTaskClick: (task: TaskCard) => void;
  onCreateTask: () => void;
  onDragStart: (task: TaskCard) => void;
  onDragEnd: () => void;
  onDrop: (columnId: string, position: number) => void;
  isDragging: boolean;
}

/**
 * Kanban Column component for displaying tasks in a status category
 */
const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  tasks,
  onTaskClick,
  onCreateTask,
  onDragStart,
  onDragEnd,
  onDrop,
  isDragging,
}) => {
  const [dragOverIndex, setDragOverIndex] = React.useState<number | null>(null);

  /**
   * Handle drag over event for the column
   */
  const handleDragOver = (e: React.DragEvent, index?: number) => {
    e.preventDefault();
    e.stopPropagation();

    if (typeof index === 'number') {
      setDragOverIndex(index);
    }
  };

  /**
   * Handle drag leave event
   */
  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    // Only clear if we're leaving the column entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setDragOverIndex(null);
    }
  };

  /**
   * Handle drop event
   */
  const handleDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();

    onDrop(column.id, index);
    setDragOverIndex(null);
  };

  /**
   * Handle drop on empty column
   */
  const handleEmptyColumnDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    onDrop(column.id, 0);
    setDragOverIndex(null);
  };

  /**
   * Calculate WIP (Work In Progress) limit display
   */
  const getWIPLimitDisplay = (): string | null => {
    if (column.limit) {
      return `${tasks.length}/${column.limit}`;
    }
    return null;
  };

  const wipLimit = getWIPLimitDisplay();
  const isOverLimit = column.limit && tasks.length > column.limit;

  return (
    <div
      className={`kanban-column ${isDragging ? 'dragging' : ''}`}
      onDragOver={tasks.length === 0 ? handleDragOver : undefined}
      onDrop={tasks.length === 0 ? handleEmptyColumnDrop : undefined}
      onDragLeave={handleDragLeave}
    >
      {/* Column Header */}
      <div
        className="kanban-column-header"
        style={{ borderTopColor: column.color }}
      >
        <div className="column-header-left">
          <div
            className="column-indicator"
            style={{ backgroundColor: column.color }}
          />
          <h3 className="column-title">{column.title}</h3>
          <span className={`column-count ${isOverLimit ? 'over-limit' : ''}`}>
            {wipLimit || tasks.length}
          </span>
        </div>

        <Button
          variant="link"
          size="sm"
          className="add-task-btn"
          onClick={onCreateTask}
          title="Add new task"
        >
          <i className="bi bi-plus-lg"></i>
        </Button>
      </div>

      {/* Column Content - Task Cards */}
      <div className="kanban-column-content">
        {tasks.length === 0 ? (
          <div className="column-empty-state">
            <i className="bi bi-inbox"></i>
            <p>No tasks</p>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={onCreateTask}
            >
              <i className="bi bi-plus-lg me-2"></i>
              Add Task
            </Button>
          </div>
        ) : (
          tasks.map((task, index) => (
            <React.Fragment key={task.id}>
              {/* Drop zone indicator */}
              {dragOverIndex === index && (
                <div className="drop-zone-indicator" />
              )}

              {/* Task Card */}
              <div
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
              >
                <TaskCardComponent
                  task={task}
                  onClick={() => onTaskClick(task)}
                  onDragStart={() => onDragStart(task)}
                  onDragEnd={onDragEnd}
                />
              </div>
            </React.Fragment>
          ))
        )}

        {/* Drop zone at the end */}
        {dragOverIndex === tasks.length && (
          <div className="drop-zone-indicator" />
        )}
        <div
          className="column-drop-zone-end"
          onDragOver={(e) => handleDragOver(e, tasks.length)}
          onDrop={(e) => handleDrop(e, tasks.length)}
        />
      </div>
    </div>
  );
};

export default KanbanColumn;

